import { Node } from 'reactflow';
import { getCollapsedState } from '@components/MainStage/collapsedLayers.store';
import { canvasState } from '@components/MainStage/store';
import { CANVAS_DEFAULT_HEIGHT } from '@constants/canvas/general';
import { subLayersNodesByMode } from '@constants/canvas/layerNodes';
import { LayerTypes, Modes, SubLayerTypes } from '@constants/canvas/layers';
import { setCanvasHeight } from '@store/slices/uiSlice';
import { store } from '@store/store';
import {
  applyCanvasGeometry,
  computeCanvasGeometry,
} from '@utils/canvas/layerHeights';
import SubLayerViewManager from '@utils/canvas/SubLayerViewManager';

class CanvasModelRenderer {
  /**
   * Recomputes the whole layer stack from the nodes currently in the canvas
   * store and writes positions and heights back in a single store update. It is
   * pure with respect to the DOM, so it is safe to call before React has
   * committed and produces the same result whether or not elements are mounted.
   */
  static async redrawFullCanvasLayers(mode: Modes) {
    const { isCollapsed } = getCollapsedState();
    const { nodes, setNodes } = canvasState();

    const geometry = computeCanvasGeometry(nodes, mode, isCollapsed);

    setNodes(applyCanvasGeometry(nodes, geometry));

    CanvasModelRenderer.resetCanvasHeight(mode);
  }

  /**
   * Delegates to a full recompute of the layer stack; `startLayerId` is
   * accepted for call-site compatibility but otherwise unused.
   */
  static async adjustBellowLayers(
    mode: Modes,
    startLayerId: SubLayerTypes | LayerTypes,
  ) {
    return CanvasModelRenderer.redrawFullCanvasLayers(mode);
  }

  static async redrawAfterSubLayerChanges(
    mode: Modes,
    subLayerId: SubLayerTypes,
  ) {
    return CanvasModelRenderer.redrawFullCanvasLayers(mode);
  }

  static removeEmptyRows(mode: Modes, filteredNodes: Node[]) {
    const layers = subLayersNodesByMode[mode];

    const visibleNodes = filteredNodes.filter((node) => !node.hidden);
    canvasState().setNodes(visibleNodes);

    layers.forEach((layerNode) => {
      SubLayerViewManager.getInstance(
        layerNode.id as SubLayerTypes,
      )?.collapseRows();
    });

    this.redrawFullCanvasLayers(mode);
  }

  static calculateCanvasHeight(mode: Modes | null) {
    if (!mode) return CANVAS_DEFAULT_HEIGHT;

    const { isCollapsed } = getCollapsedState();

    return computeCanvasGeometry(canvasState().nodes, mode, isCollapsed)
      .canvasHeight;
  }

  static resetCanvasHeight(mode: Modes | null) {
    const height = CanvasModelRenderer.calculateCanvasHeight(mode);
    store.dispatch(setCanvasHeight(height));
  }
}

export default CanvasModelRenderer;
